/**
 * TUVI - Proyectos Especiales
 * Tabla tipo Excel: edición inline (click en celda), KPIs arriba, exportar Excel.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import XLSX from 'xlsx-js-style';
import './ProyectosEspeciales.css';

const ESTADOS = [
  { value: 'en_ejecucion', label: 'En Ejecución' },
  { value: 'finalizado', label: 'Finalizado' },
];

const fmt = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', minimumFractionDigits: 0,
}).format(Number(n || 0));

const fmtPct = (n) => `${(Number(n || 0)).toFixed(2)}%`;

const parseNum = (s) => {
  if (s === '' || s === null || s === undefined) return 0;
  const clean = String(s).replace(/[^\d.-]/g, '');
  const n = Number(clean);
  return isNaN(n) ? 0 : n;
};

const QUERY = `query($propietario: String, $estado: String, $busqueda: String) {
  proyectosEspeciales(propietario: $propietario, estado: $estado, busqueda: $busqueda) {
    items {
      id proyecto_id estado propietario proyecto apartamento
      valor_total valor_pagado_cliente valor_ejecutado
      por_recaudar por_ejecutar porcentaje_pago
      incluir_en_cartera notas orden created_at
    }
    resumen {
      valor margen pagado_cliente valor_ejecutado
      por_recaudar por_ejecutar utilidad cartera cantidad
    }
    propietarios
  }
}`;

const MUT_CREAR = `mutation($input: ProyectoEspecialInput!) {
  crearProyectoEspecial(input: $input) { success message id }
}`;
const MUT_UPDATE = `mutation($id: Int!, $input: ProyectoEspecialInput!) {
  actualizarProyectoEspecial(id: $id, input: $input) { success message }
}`;
const MUT_DEL = `mutation($id: Int!) {
  eliminarProyectoEspecial(id: $id) { success message }
}`;

const ProyectosEspeciales = ({ onVerDetalle }) => {
  const { success, error } = useNotification();
  const [data, setData] = useState({ items: [], resumen: null, propietarios: [] });
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ propietario: '', estado: '', busqueda: '' });
  const [editando, setEditando] = useState(null); // { id, campo }
  const [editValue, setEditValue] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const vars = {};
      if (filtros.propietario) vars.propietario = filtros.propietario;
      if (filtros.estado) vars.estado = filtros.estado;
      if (filtros.busqueda) vars.busqueda = filtros.busqueda;
      const res = await client.query(QUERY, vars);
      setData(res.proyectosEspeciales);
    } catch (e) {
      error('Error al cargar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filtros, error]);

  useEffect(() => { cargar(); }, [cargar]);

  const iniciarEdicion = (id, campo, valorActual) => {
    setEditando({ id, campo });
    setEditValue(valorActual ?? '');
  };

  const cancelarEdicion = () => { setEditando(null); setEditValue(''); };

  const guardarEdicion = async () => {
    if (!editando) return;
    const { id, campo } = editando;
    const item = data.items.find(i => i.id === id);
    if (!item) { cancelarEdicion(); return; }

    let nuevoValor = editValue;
    if (['valor_total', 'valor_pagado_cliente', 'valor_ejecutado'].includes(campo)) {
      nuevoValor = parseNum(editValue);
    }

    // Si no cambió, solo cerrar
    if (nuevoValor === item[campo]) { cancelarEdicion(); return; }

    // Optimistic update
    setData(prev => ({ ...prev, items: prev.items.map(i => i.id === id ? { ...i, [campo]: nuevoValor } : i) }));
    cancelarEdicion();

    try {
      const input = { propietario: item.propietario, [campo]: nuevoValor };
      const r = await client.mutate(MUT_UPDATE, { id, input });
      if (r.actualizarProyectoEspecial.success) {
        cargar(); // recargar para que los totales y derivados queden exactos
      } else {
        error(r.actualizarProyectoEspecial.message || 'Error al guardar');
        cargar();
      }
    } catch (e) {
      error('Error al guardar');
      cargar();
    }
  };

  const toggleCartera = async (item) => {
    const nuevo = !item.incluir_en_cartera;
    setData(prev => ({ ...prev, items: prev.items.map(i => i.id === item.id ? { ...i, incluir_en_cartera: nuevo } : i) }));
    try {
      await client.mutate(MUT_UPDATE, { id: item.id, input: { propietario: item.propietario, incluir_en_cartera: nuevo } });
      cargar();
    } catch {
      error('Error al actualizar');
      cargar();
    }
  };

  const agregarFila = async () => {
    try {
      const r = await client.mutate(MUT_CREAR, {
        input: { propietario: 'Nuevo', estado: 'en_ejecucion', valor_total: 0, valor_pagado_cliente: 0, valor_ejecutado: 0, incluir_en_cartera: true },
      });
      if (r.crearProyectoEspecial.success) {
        success('Fila agregada. Edita los valores haciendo click.');
        cargar();
      }
    } catch (e) {
      error('Error al crear');
    }
  };

  const eliminarFila = async (id, propietario) => {
    if (!window.confirm(`¿Eliminar "${propietario}"?`)) return;
    try {
      const r = await client.mutate(MUT_DEL, { id });
      if (r.eliminarProyectoEspecial.success) {
        success('Eliminado');
        cargar();
      }
    } catch {
      error('Error al eliminar');
    }
  };

  const handleInputChange = (e) => setEditValue(e.target.value);
  const handleInputKey = (e) => {
    if (e.key === 'Enter') guardarEdicion();
    if (e.key === 'Escape') cancelarEdicion();
  };

  // ===== Exportar Excel con formato profesional =====
  const exportarExcel = () => {
    const r = data.resumen || {};
    const wb = XLSX.utils.book_new();
    const wsData = [];
    const merges = [];
    const styles = {};

    const estiloTitulo = { font: { bold: true, sz: 18, color: { rgb: '1A2432' } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const estiloSub = { font: { sz: 11, color: { rgb: '555555' } }, alignment: { horizontal: 'left' } };
    const estiloHeader = {
      font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1A2432' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: 'FFFFFF' } },
        right: { style: 'thin', color: { rgb: 'FFFFFF' } },
      },
    };
    const estiloCelda = {
      font: { sz: 10 },
      alignment: { vertical: 'center' },
      border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
    };
    const estiloMoney = { ...estiloCelda, numFmt: '"$"#,##0', alignment: { horizontal: 'right', vertical: 'center' } };
    const estiloPct = { ...estiloCelda, numFmt: '0.00%', alignment: { horizontal: 'right', vertical: 'center' } };
    const estiloTotal = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '2E7D32' } },
      alignment: { horizontal: 'right', vertical: 'center' },
      numFmt: '"$"#,##0',
    };
    const estiloTotalLabel = { ...estiloTotal, alignment: { horizontal: 'left', vertical: 'center' } };
    const estiloKPILabel = {
      font: { bold: true, sz: 10, color: { rgb: '1A2432' } },
      fill: { fgColor: { rgb: 'F5F5F5' } },
      alignment: { horizontal: 'left', vertical: 'center' },
      border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
    };
    const estiloKPIVal = {
      font: { bold: true, sz: 11, color: { rgb: '2E7D32' } },
      alignment: { horizontal: 'right', vertical: 'center' },
      numFmt: '"$"#,##0',
      border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } },
    };

    const put = (row, col, v) => {
      if (!wsData[row]) wsData[row] = [];
      wsData[row][col] = v;
    };
    const setStyle = (row, col, s) => {
      styles[XLSX.utils.encode_cell({ r: row, c: col })] = s;
    };

    // Título
    put(0, 0, 'TUVI - PROYECTOS ESPECIALES');
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 8 } });
    setStyle(0, 0, estiloTitulo);
    put(1, 0, `Generado: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}  |  ${data.items.length} proyectos`);
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 8 } });
    setStyle(1, 0, estiloSub);

    // KPIs en una tabla 2x4
    const kpis = [
      ['VALOR', r.valor], ['MARGEN', r.margen],
      ['PAGADO CLIENTE', r.pagado_cliente], ['VALOR EJECUTADO', r.valor_ejecutado],
      ['POR RECAUDAR', r.por_recaudar], ['POR EJECUTAR', r.por_ejecutar],
      ['UTILIDAD', r.utilidad], ['CARTERA', r.cartera],
    ];
    let rKpi = 3;
    kpis.forEach(([label, val], idx) => {
      const row = rKpi + Math.floor(idx / 2);
      const col = (idx % 2) * 4;
      put(row, col, label);
      put(row, col + 1, Number(val || 0));
      setStyle(row, col, estiloKPILabel);
      setStyle(row, col + 1, estiloKPIVal);
      merges.push({ s: { r: row, c: col }, e: { r: row, c: col + 1 } }); // merge label con 1 col
      merges.push({ s: { r: row, c: col + 2 }, e: { r: row, c: col + 3 } }); // merge val (visual)
    });
    const rTabla = rKpi + 5;

    // Headers
    const headers = ['Estado', 'Propietario', 'Proyecto', 'Apto', '% Pago', 'Valor Total', 'Valor Pagado', 'Valor Ejecutado', 'Por Recaudar'];
    headers.forEach((h, i) => { put(rTabla, i, h); setStyle(rTabla, i, estiloHeader); });

    // Filas
    data.items.forEach((it, idx) => {
      const row = rTabla + 1 + idx;
      const estadoLbl = (ESTADOS.find(e => e.value === it.estado) || {}).label || it.estado;
      put(row, 0, estadoLbl);
      put(row, 1, it.propietario || '');
      put(row, 2, it.proyecto || '');
      put(row, 3, it.apartamento || '');
      put(row, 4, (Number(it.porcentaje_pago || 0)) / 100);
      put(row, 5, Number(it.valor_total || 0));
      put(row, 6, Number(it.valor_pagado_cliente || 0));
      put(row, 7, Number(it.valor_ejecutado || 0));
      put(row, 8, Number(it.por_recaudar || 0));
      for (let c = 0; c < 9; c++) {
        if (c === 4) setStyle(row, c, estiloPct);
        else if (c >= 5) setStyle(row, c, estiloMoney);
        else setStyle(row, c, estiloCelda);
      }
    });

    // Fila total
    const rTotal = rTabla + 1 + data.items.length;
    put(rTotal, 0, 'TOTALES');
    put(rTotal, 1, '');
    put(rTotal, 2, '');
    put(rTotal, 3, '');
    put(rTotal, 4, '');
    put(rTotal, 5, Number(r.valor || 0));
    put(rTotal, 6, Number(r.pagado_cliente || 0));
    put(rTotal, 7, Number(r.valor_ejecutado || 0));
    put(rTotal, 8, Number(r.por_recaudar || 0));
    for (let c = 0; c < 9; c++) {
      setStyle(rTotal, c, c === 0 ? estiloTotalLabel : estiloTotal);
    }
    merges.push({ s: { r: rTotal, c: 0 }, e: { r: rTotal, c: 4 } });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    Object.entries(styles).forEach(([cell, s]) => { if (ws[cell]) ws[cell].s = s; });
    ws['!cols'] = [
      { wch: 16 }, { wch: 34 }, { wch: 30 }, { wch: 10 },
      { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
    ];
    ws['!merges'] = merges;
    ws['!rows'] = wsData.map((_, i) => ({ hpt: i === 0 ? 30 : 20 }));

    XLSX.utils.book_append_sheet(wb, ws, 'Proyectos Especiales');
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Proyectos_Especiales_TUVI_${fecha}.xlsx`);
    success('Excel exportado');
  };

  const { items, resumen, propietarios } = data;

  // Render celda editable
  const celdaEditable = (item, campo, displayValue, tipo = 'text') => {
    const enEdicion = editando?.id === item.id && editando?.campo === campo;
    if (enEdicion) {
      return (
        <input
          type={tipo}
          className="pe-inline-input"
          value={editValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKey}
          onBlur={guardarEdicion}
          autoFocus
        />
      );
    }
    return (
      <span
        className="pe-cell-editable"
        onClick={() => iniciarEdicion(item.id, campo, item[campo] ?? '')}
        title="Click para editar"
      >
        {displayValue || <span className="pe-empty">—</span>}
      </span>
    );
  };

  const celdaSelect = (item, campo, opciones) => {
    const enEdicion = editando?.id === item.id && editando?.campo === campo;
    if (enEdicion) {
      return (
        <select
          className="pe-inline-select"
          value={editValue}
          onChange={(e) => { setEditValue(e.target.value); }}
          onBlur={guardarEdicion}
          autoFocus
        >
          {opciones.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    const opt = opciones.find(o => o.value === item[campo]);
    return (
      <span
        className={`pe-cell-editable pe-estado-badge pe-estado-${item[campo] || 'en_ejecucion'}`}
        onClick={() => iniciarEdicion(item.id, campo, item[campo] || 'en_ejecucion')}
      >
        {opt?.label || item[campo]}
      </span>
    );
  };

  return (
    <div className="pe-container">
      <div className="pe-header">
        <div>
          <h1>Proyectos Especiales</h1>
          <p className="pe-sub">{items.length} proyecto(s) registrados</p>
        </div>
        <div className="pe-header-actions">
          <button className="pe-btn pe-btn-excel" onClick={exportarExcel} disabled={items.length === 0}>
            📊 Exportar Excel
          </button>
          <button className="pe-btn pe-btn-primary" onClick={agregarFila}>
            + Nueva fila
          </button>
        </div>
      </div>

      {/* KPIs */}
      {resumen && (
        <div className="pe-kpis">
          <div className="pe-kpi"><span>VALOR</span><strong>{fmt(resumen.valor)}</strong></div>
          <div className="pe-kpi pe-kpi-margen"><span>MARGEN (75%)</span><strong>{fmt(resumen.margen)}</strong></div>
          <div className="pe-kpi"><span>PAGADO CLIENTE</span><strong>{fmt(resumen.pagado_cliente)}</strong></div>
          <div className="pe-kpi"><span>VALOR EJECUTADO</span><strong>{fmt(resumen.valor_ejecutado)}</strong></div>
          <div className="pe-kpi pe-kpi-warning"><span>POR RECAUDAR</span><strong>{fmt(resumen.por_recaudar)}</strong></div>
          <div className="pe-kpi pe-kpi-warning"><span>POR EJECUTAR</span><strong>{fmt(resumen.por_ejecutar)}</strong></div>
          <div className="pe-kpi pe-kpi-success"><span>UTILIDAD (25%)</span><strong>{fmt(resumen.utilidad)}</strong></div>
          <div className="pe-kpi pe-kpi-cartera"><span>CARTERA</span><strong>{fmt(resumen.cartera)}</strong></div>
        </div>
      )}

      {/* Filtros */}
      <div className="pe-filtros">
        <select value={filtros.propietario} onChange={(e) => setFiltros(f => ({ ...f, propietario: e.target.value }))}>
          <option value="">Todos los propietarios</option>
          {propietarios.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filtros.estado} onChange={(e) => setFiltros(f => ({ ...f, estado: e.target.value }))}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
        <input
          type="text"
          placeholder="Buscar por propietario, proyecto, apto, notas..."
          value={filtros.busqueda}
          onChange={(e) => setFiltros(f => ({ ...f, busqueda: e.target.value }))}
        />
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="pe-loading">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="pe-empty-state">
          <p>Sin proyectos especiales aún.</p>
          <button className="pe-btn pe-btn-primary" onClick={agregarFila}>+ Crear el primero</button>
        </div>
      ) : (
        <div className="pe-table-wrap">
          <table className="pe-table">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Propietario</th>
                <th>Proyecto</th>
                <th>Apto</th>
                <th className="pe-col-num">% Pago</th>
                <th className="pe-col-num">Valor Total</th>
                <th className="pe-col-num">Valor Pagado</th>
                <th className="pe-col-num">Valor Ejecutado</th>
                <th className="pe-col-num">Por Recaudar</th>
                <th className="pe-col-num">Por Ejecutar</th>
                <th className="pe-col-check">En Cartera</th>
                <th className="pe-col-acc">Acc.</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className={!it.incluir_en_cartera ? 'pe-row-excluida' : ''}>
                  <td>{celdaSelect(it, 'estado', ESTADOS)}</td>
                  <td>{celdaEditable(it, 'propietario', it.propietario)}</td>
                  <td>{celdaEditable(it, 'proyecto', it.proyecto)}</td>
                  <td>{celdaEditable(it, 'apartamento', it.apartamento)}</td>
                  <td className="pe-col-num pe-pct">{fmtPct(it.porcentaje_pago)}</td>
                  <td className="pe-col-num">{celdaEditable(it, 'valor_total', fmt(it.valor_total), 'number')}</td>
                  <td className="pe-col-num">
                    {it.proyecto_id
                      ? <span className="pe-auto" title="Calculado automáticamente desde la cartera del proyecto">{fmt(it.valor_pagado_cliente)}</span>
                      : celdaEditable(it, 'valor_pagado_cliente', fmt(it.valor_pagado_cliente), 'number')}
                  </td>
                  <td className="pe-col-num">
                    {it.proyecto_id
                      ? <span className="pe-auto" title="Calculado automáticamente desde las facturas del proyecto">{fmt(it.valor_ejecutado)}</span>
                      : celdaEditable(it, 'valor_ejecutado', fmt(it.valor_ejecutado), 'number')}
                  </td>
                  <td className="pe-col-num pe-highlight-warning">{fmt(it.por_recaudar)}</td>
                  <td className="pe-col-num pe-highlight-warning">{fmt(it.por_ejecutar)}</td>
                  <td className="pe-col-check">
                    <input
                      type="checkbox"
                      checked={!!it.incluir_en_cartera}
                      onChange={() => toggleCartera(it)}
                      title="Incluir en cálculo de CARTERA"
                    />
                  </td>
                  <td className="pe-col-acc">
                    {it.proyecto_id && (
                      <button
                        className="pe-btn-del"
                        onClick={() => { if (onVerDetalle) onVerDetalle(it.proyecto_id); }}
                        title="Ver detalle del proyecto (capítulos, presupuesto, facturas)"
                      >👁️</button>
                    )}
                    <button className="pe-btn-del" onClick={() => eliminarFila(it.id, it.propietario)} title="Eliminar">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {resumen && (
              <tfoot>
                <tr>
                  <td colSpan="4"><strong>TOTALES</strong></td>
                  <td></td>
                  <td className="pe-col-num"><strong>{fmt(resumen.valor)}</strong></td>
                  <td className="pe-col-num"><strong>{fmt(resumen.pagado_cliente)}</strong></td>
                  <td className="pe-col-num"><strong>{fmt(resumen.valor_ejecutado)}</strong></td>
                  <td className="pe-col-num"><strong>{fmt(resumen.por_recaudar)}</strong></td>
                  <td className="pe-col-num"><strong>{fmt(resumen.por_ejecutar)}</strong></td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default ProyectosEspeciales;
